/* Platform admin: storage and email settings, email log, templates, DNS guidance, suppressions.
 * Validation mirrors api/internal/api/h_admin_storage.go + h_admin_email.go. The mock cannot reach S3 or a mail
 * server, so connections are simulated deterministically (documented in contracts/API.md "Mock simulation"):
 *   access key containing "INVALID" → InvalidAccessKeyId · S3 secret containing "wrong" → SignatureDoesNotMatch (credentials step) ·
 *   bucket containing "missing" → NoSuchBucket (bucket step) · access key containing "DENIED" → AccessDenied on write ·
 *   endpoint on port 1 → connection refused · email secret "wrong" → authentication failure. */
import type * as T from "../types";
import { E, has, isInt, str, type Ctx } from "./ctx";
import { CATALOG, dnsGuidance, normalizeProvider, providerInfo, renderTemplate, TEMPLATE_KEYS, validAddress, validateProvider } from "./mailer";
import type { MockEmailProvider, MockStorageState } from "./state";
import { S3_ERR, validateS3 } from "../storageRules";

const maskKey = (k: string) => (k.length < 10 ? "••••••••" : k.slice(0, 6) + "••••••••" + k.slice(-4));
const LOCAL_DIR = "/var/lib/starthr/files";
const IAM = "s3:ListBucket, s3:GetObject, s3:PutObject, s3:DeleteObject";

/* ---------- storage validation: shared with the File storage form (web/src/data/storageRules.ts = Go model/storage_validate.go) ---------- */
export { bucketProblem, endpointProblem, regionProblem, validateS3 } from "../storageRules";
const normalizePrefix = (p: string) => { const t = p.trim().replace(/^\/+|\/+$/g, ""); return t ? t + "/" : ""; };
interface ParsedStorage { st: MockStorageState; fe: Record<string, string> }

function parseStorage(c: Ctx, input: T.StorageInput | undefined): ParsedStorage {
  const cur = c.root.storageCfg;
  const m = (input ?? {}) as unknown as Record<string, unknown>;
  const fe: Record<string, string> = {};
  const s = (k: string) => { const v = m[k]; if (v != null && typeof v !== "string") fe[k] = "must be a string"; return typeof v === "string" ? v.trim() : ""; };
  const b = (k: string) => { const v = m[k]; if (v != null && typeof v !== "boolean") fe[k] = "must be true or false"; return v === true; };
  // Dashboard-saved S3 settings always use explicit keys; useInstanceRole is read-only (environment defaults only).
  const st: MockStorageState = { mode: s("mode") as T.StorageMode, bucket: s("bucket"), region: s("region").toLowerCase(), endpoint: s("endpoint").replace(/\/+$/, ""),
    pathStyle: b("pathStyle"), prefix: s("prefix"), useInstanceRole: false, presign: b("presign"), accessKeyId: "", secret: "", source: "database" };
  let keyId = s("accessKeyId");
  let sec = str(m.secretAccessKey).trim();
  if (has(m, "migrate") && m.migrate !== false) fe.migrate = "moving existing files is not supported — copy them with `aws s3 sync`, then switch";
  if (st.mode === "memory" || st.mode === "local") return { st: { ...st, bucket: "", region: "", endpoint: "", prefix: "", pathStyle: false }, fe };
  if (!st.mode) { fe.mode = "required"; return { st, fe }; }
  if (st.mode !== "s3") { fe.mode = "must be s3, local or memory"; return { st, fe }; }
  if (keyId.includes("•")) keyId = cur.accessKeyId; // masked echo of the stored key → keep it
  st.accessKeyId = keyId;
  const newKey = keyId !== cur.accessKeyId;
  if (!sec && !newKey) sec = cur.secret; // write-only: empty keeps the stored secret for the same key
  for (const [k, v] of Object.entries(validateS3(st, sec))) if (!fe[k]) fe[k] = v;
  if (fe.secretAccessKey === S3_ERR.required && newKey && cur.accessKeyId && cur.secret && keyId) fe.secretAccessKey = S3_ERR.secretNewKey;
  if (!fe.prefix) st.prefix = normalizePrefix(st.prefix);
  st.secret = sec;
  return { st, fe };
}

/** Simulated connection test (see the header comment). S3: credentials, bucket, write, read, delete; local/memory: connect, write, read, delete. */
function simulateProbe(st: MockStorageState): T.StorageTestResult {
  const steps: T.StorageTestStep[] = [];
  const names: T.StorageTestStep["step"][] = st.mode === "s3" ? ["credentials", "bucket", "write", "read", "delete"] : ["connect", "write", "read", "delete"];
  let failAt = -1, error = "", hint = "";
  if (st.mode === "s3") {
    if (/:1\/?$/.test(st.endpoint)) { failAt = 0; error = "dial tcp 127.0.0.1:1: connect: connection refused"; hint = "Connection refused. Check the endpoint URL and port (MinIO usually listens on :9000)."; }
    else if (st.accessKeyId.toUpperCase().includes("INVALID")) { failAt = 0; error = "InvalidAccessKeyId (HTTP 403): The AWS Access Key Id you provided does not exist in our records."; hint = "The access key ID is not recognised. Check it, or that the key is active in IAM (Users → Security credentials)."; }
    else if (st.secret.toLowerCase().includes("wrong")) { failAt = 0; error = "SignatureDoesNotMatch (HTTP 403): The request signature we calculated does not match the signature you provided."; hint = "The secret access key is wrong for this access key ID. Paste it again (no spaces)."; }
    else if (st.bucket.includes("missing")) { failAt = 1; error = "NoSuchBucket (HTTP 404): The specified bucket does not exist"; hint = "The bucket does not exist (or is in another account). Check the bucket name, or create it in the S3 console."; }
    else if (st.accessKeyId.toUpperCase().includes("DENIED")) {
      failAt = 2; error = "AccessDenied (HTTP 403): Access Denied";
      hint = `Access denied. The IAM user or instance role needs ${IAM} on the bucket (ListBucket on arn:aws:s3:::<bucket>, the object actions on arn:aws:s3:::<bucket>/*). Also check the bucket policy and, for KMS-encrypted buckets, kms:Encrypt/kms:Decrypt/kms:GenerateDataKey on the key.`;
    }
  }
  names.forEach((n, i) => {
    if (failAt >= 0 && i > failAt) steps.push({ step: n, ok: false, ms: 0, skipped: true });
    else if (i === failAt) steps.push({ step: n, ok: false, ms: 12, error, hint });
    else steps.push({ step: n, ok: true, ms: 3 + i });
  });
  return { ok: failAt < 0, mode: st.mode, steps };
}

/* ---------- email config parsing (Go parseEmailInput) ---------- */
function parseProvider(m: Record<string, unknown>, prefix: string, prev: MockEmailProvider | null, fe: Record<string, string>): MockEmailProvider {
  const s = (k: string) => { const v = m[k]; if (v != null && typeof v !== "string") fe[prefix + k] = "must be a string"; return typeof v === "string" ? v.trim() : ""; };
  let port = 0;
  if (m.port != null) { if (isInt(m.port)) port = m.port; else fe[prefix + "port"] = "must be an integer"; }
  const p: MockEmailProvider = { provider: s("provider").toLowerCase() as T.EmailProviderId, host: s("host").toLowerCase(), port, security: s("security").toLowerCase() as T.SmtpSecurity,
    authMethod: s("authMethod").toLowerCase() as "plain" | "login", username: s("username"), secret: str(m.secret).trim(), region: s("region"), domain: s("domain").toLowerCase(),
    messageStream: s("messageStream"), endpoint: s("endpoint").replace(/\/+$/, "") };
  if (!p.secret && prev && prev.provider === p.provider) p.secret = prev.secret;
  const n = normalizeProvider(p);
  if (!n.provider) { fe[prefix + "provider"] = "required"; return n; }
  Object.assign(fe, validateProvider(n, prefix));
  return n;
}

function parseEmail(c: Ctx, input: T.EmailConfigInput) {
  const cur = c.root.emailCfg;
  const m = (input ?? {}) as unknown as Record<string, unknown>;
  const fe: Record<string, string> = {};
  let provider: MockEmailProvider = { ...cur.provider };
  if (m.provider == null || typeof m.provider !== "object") fe["provider.provider"] = "required";
  else provider = parseProvider(m.provider as Record<string, unknown>, "provider.", cur.provider, fe);
  let fallback: MockEmailProvider | null = null;
  if (m.fallback != null) {
    if (typeof m.fallback !== "object" || Array.isArray(m.fallback)) fe.fallback = "must be an object or null";
    else {
      fallback = parseProvider(m.fallback as Record<string, unknown>, "fallback.", cur.fallback, fe);
      if (fallback.provider === provider.provider) fe["fallback.provider"] = "must differ from the main provider";
      if (fallback.provider === "log") fe["fallback.provider"] = "Log only cannot be a fallback";
    }
  }
  const fromName = str(m.fromName).trim(), fromAddress = str(m.fromAddress).trim().toLowerCase(), replyTo = str(m.replyTo).trim().toLowerCase(), sandboxTo = str(m.sandboxTo).trim().toLowerCase();
  if (!fromName) fe.fromName = "required"; else if ([...fromName].length > 100) fe.fromName = "max 100 characters";
  if (!validAddress(fromAddress)) fe.fromAddress = "a valid email address is required";
  if (replyTo && !validAddress(replyTo)) fe.replyTo = "must be a valid email address";
  if (sandboxTo && !validAddress(sandboxTo)) fe.sandboxTo = "must be a valid email address";
  let dailyLimit = 0;
  if (m.dailyLimit != null) { if (!isInt(m.dailyLimit) || m.dailyLimit < 0 || m.dailyLimit > 1_000_000) fe.dailyLimit = "must be an integer between 0 (no limit) and 1,000,000"; else dailyLimit = m.dailyLimit; }
  return { cfg: { provider, fallback, fromName, fromAddress, replyTo, sandboxTo, dailyLimit, source: "database" as const }, fe };
}

const providerView = (p: MockEmailProvider): T.EmailProviderSettings => {
  const { secret, ...rest } = p;
  return { ...rest, hasSecret: !!secret };
};
const STATUSES = ["queued", "sending", "sent", "failed", "suppressed"];

export function platformOps(c: Ctx) {
  const storageView = (): T.StorageConfig => {
    const s = c.root.storageCfg;
    const { secret, accessKeyId, ...rest } = s;
    const objects = s.mode === "s3" ? undefined : c.root.files.size;
    return { ...rest, accessKeyId: accessKeyId ? maskKey(accessKeyId) : "", hasSecret: !!secret, dir: s.mode === "local" ? LOCAL_DIR : "",
      status: { ok: true, message: s.mode === "memory" ? "In memory — files are lost when the server restarts (demo only)" : "Connected", checkedAt: new Date(c.wallNow()).toISOString().replace(/\.\d{3}Z$/, "Z") },
      ...(objects != null ? { stats: { objects } } : {}) };
  };
  const emailView = (): T.EmailConfig => {
    const e = c.root.emailCfg;
    const day = new Date(c.wallNow()).toISOString().slice(0, 10);
    return { provider: providerView(e.provider), fallback: e.fallback ? providerView(e.fallback) : null, fromName: e.fromName, fromAddress: e.fromAddress, replyTo: e.replyTo,
      sandboxTo: e.sandboxTo, dailyLimit: e.dailyLimit, source: e.source, sentToday: c.root.outbox.filter((m) => m.status === "sent" && (m.sentAt ?? "").startsWith(day)).length,
      queued: c.root.outbox.filter((m) => m.status === "queued").length, ...(e.updatedAt ? { updatedAt: e.updatedAt, updatedBy: e.updatedBy } : {}) };
  };
  const pubLog = (m: (typeof c.root.outbox)[number]): T.EmailLog => {
    const { html: _h, text: _t, idemKey: _i, unsubscribe: _u, ...pub } = m;
    void _h; void _t; void _i; void _u;
    return pub;
  };
  const nowIso = () => new Date(c.wallNow()).toISOString().replace(/\.\d{3}Z$/, "Z");

  return {
    /* ---------- storage ---------- */
    async adminStorage(): Promise<T.StorageConfig> { c.principal("admin"); return storageView(); },

    async updateAdminStorage(input: T.StorageInput): Promise<T.StorageConfig> {
      const u = c.principal("admin");
      if (input == null || typeof input !== "object") throw E.badRequest("JSON object expected");
      const { st, fe } = parseStorage(c, input);
      if (Object.keys(fe).length) throw E.validation(fe);
      if (!input.force) {
        const probe = simulateProbe(st);
        // Go pings the bucket before saving: a credentials/bucket/connect failure blocks the save (write/read/delete do not)
        const bad = probe.steps.find((x) => !x.ok && !x.skipped && (x.step === "credentials" || x.step === "bucket" || x.step === "connect"));
        if (bad) throw E.field("connection", `${bad.error} — ${bad.hint}`);
      }
      const prev = c.root.storageCfg;
      c.root.storageCfg = { ...st, updatedAt: nowIso(), updatedBy: u.name };
      let detail = st.mode + (st.mode === "s3" ? ` ${st.bucket} (${st.region})` : "");
      if (prev.mode === "s3" && st.mode === "s3" && prev.bucket !== st.bucket) detail += " · bucket changed from " + prev.bucket;
      c.audit(u, "Updated storage settings", "Storage", st.mode, detail);
      return storageView();
    },

    async resetAdminStorage(): Promise<T.StorageConfig> {
      const u = c.principal("admin");
      c.root.storageCfg = { mode: "memory", bucket: "", region: "", endpoint: "", pathStyle: false, prefix: "", accessKeyId: "", secret: "", useInstanceRole: false, presign: false, source: "env" };
      c.audit(u, "Reset storage settings", "Storage", "memory", "back to environment defaults");
      return storageView();
    },

    async testAdminStorage(input?: T.StorageInput): Promise<T.StorageTestResult> {
      c.principal("admin");
      if (!input || Object.keys(input).length === 0) return simulateProbe(c.root.storageCfg);
      const { st, fe } = parseStorage(c, input);
      if (Object.keys(fe).length) throw E.validation(fe);
      return simulateProbe(st);
    },

    /* ---------- email ---------- */
    async adminEmail(): Promise<T.EmailConfig> { c.principal("admin"); return emailView(); },

    async updateAdminEmail(input: T.EmailConfigInput): Promise<T.EmailConfig> {
      const u = c.principal("admin");
      const { cfg, fe } = parseEmail(c, input);
      if (Object.keys(fe).length) throw E.validation(fe);
      c.root.emailCfg = { ...cfg, updatedAt: nowIso(), updatedBy: u.name };
      c.audit(u, "Updated email settings", "Email", cfg.provider.provider, `${cfg.provider.provider} · ${cfg.fromAddress}${cfg.sandboxTo ? " · sandbox → " + cfg.sandboxTo : ""}`);
      return emailView();
    },

    async resetAdminEmail(): Promise<T.EmailConfig> {
      const u = c.principal("admin");
      c.root.emailCfg = { provider: { provider: "log", host: "", port: 0, security: "", authMethod: "", username: "", region: "", domain: "", messageStream: "", endpoint: "", secret: "" },
        fallback: null, fromName: "StartHR", fromAddress: "noreply@starthr.local", replyTo: "", sandboxTo: "", dailyLimit: 0, source: "env" };
      c.audit(u, "Reset email settings", "Email", "log", "back to environment defaults");
      return emailView();
    },

    async testAdminEmail(req: T.EmailTestRequest): Promise<T.EmailTestResult> {
      const u = c.principal("admin");
      const to = str(req?.to).trim().toLowerCase();
      const fe: Record<string, string> = {};
      if (!validAddress(to)) fe.to = "a valid email address is required";
      const lang = str(req?.lang);
      if (lang && lang !== "en" && lang !== "ar") fe.lang = "must be en or ar";
      let provider = c.root.emailCfg.provider;
      if (req?.config != null) {
        const p = parseEmail(c, req.config);
        for (const [k, v] of Object.entries(p.fe)) fe["config." + k] = v;
        provider = p.cfg.provider;
      }
      if (Object.keys(fe).length) throw E.validation(fe);
      const info = providerInfo(provider.provider);
      const out = renderTemplate("test", lang === "ar" ? "ar" : "en", { Name: u.name, Provider: info?.name ?? provider.provider, SentAt: new Date(c.wallNow() + 3 * 3600_000).toISOString().slice(0, 16).replace("T", " "), Link: c.baseUrl + "/#/admin/email" });
      const now = nowIso();
      const failed = provider.secret === "wrong";
      const id = "em-test-" + String(c.root.outbox.length + 1).padStart(4, "0");
      const providerId = failed ? "" : provider.provider + "-" + Math.random().toString(16).slice(2, 10);
      c.root.outbox.push({ id, tenantId: "platform", tenant: "StartHR", to, toName: u.name, lang: lang === "ar" ? "ar" : "en", template: "test", category: "test", essential: true, subject: out.subject,
        status: failed ? "failed" : "sent", attempts: 1, nextAttemptAt: now, providerId, provider: provider.provider, error: failed ? `${provider.provider}: 535 Authentication failed` : "",
        createdAt: now, sentAt: failed ? null : now, html: out.html, text: out.text, idemKey: "test:" + id, unsubscribe: "" });
      c.audit(u, "Sent test email", "Email", provider.provider, `${to} · ${failed ? "failed" : "ok"}`);
      return failed
        ? { ok: false, provider: provider.provider, providerMessageId: "", ms: 41, to, logId: id, error: `${provider.provider}: 535 Authentication failed`, errorKind: "auth",
          hint: "Authentication failed. Check the username/password or API key, and that the key has permission to send email." }
        : { ok: true, provider: provider.provider, providerMessageId: providerId, ms: 37, to, logId: id };
    },

    async adminEmailLogs(q: T.EmailLogQuery = {}): Promise<T.Page<T.EmailLog>> {
      c.principal("admin");
      const page = q.page ?? 1, size = Math.min(q.pageSize ?? 25, 1000);
      const fe: Record<string, string> = {};
      if (!isInt(page) || page < 1) fe.page = "must be an integer ≥ 1";
      if (!isInt(size) || size < 1) fe.pageSize = "must be an integer between 1 and 1000";
      if (Object.keys(fe).length) throw E.validation(fe);
      if (q.status && !STATUSES.includes(q.status)) throw E.field("status", "must be queued, sending, sent, failed or suppressed");
      if (q.template && !CATALOG.templates[q.template]) throw E.field("template", "unknown template");
      const to = (q.to ?? "").trim().toLowerCase();
      const items = c.root.outbox
        .filter((m) => (!q.status || m.status === q.status) && (!q.template || m.template === q.template) && (!to || m.to.includes(to) || (m.redirectedFrom ?? "").toLowerCase().includes(to)))
        .slice().sort((a, b) => (a.createdAt !== b.createdAt ? (a.createdAt < b.createdAt ? 1 : -1) : a.id < b.id ? 1 : -1));
      return { items: items.slice((page - 1) * size, page * size).map(pubLog), total: items.length, page, pageSize: size };
    },

    async adminEmailLog(id: string): Promise<T.EmailLogDetail> {
      c.principal("admin");
      const m = c.root.outbox.find((x) => x.id === id);
      if (!m) throw E.notFound("email not found");
      const visible = c.root.emailCfg.provider.provider === "log";
      return { ...pubLog(m), bodyHidden: !visible, ...(visible ? { html: m.html, text: m.text } : {}) };
    },

    async resendEmail(id: string): Promise<T.EmailLog> {
      const u = c.principal("admin");
      const m = c.root.outbox.find((x) => x.id === id);
      if (!m) throw E.notFound("email not found");
      const now = nowIso();
      const n = { ...m, id: m.id + "-r" + (c.root.outbox.filter((x) => x.id.startsWith(m.id + "-r")).length + 1), status: "queued" as const, attempts: 0, nextAttemptAt: now, createdAt: now, sentAt: null,
        providerId: "", provider: "", error: "", idemKey: "resend:" + m.id + ":" + now, redirectedFrom: undefined };
      const sp = c.mail.suppressed(n.to, n.essential);
      if (sp) { n.status = "suppressed" as never; n.error = "suppressed: " + sp.reason; }
      c.root.outbox.push(n);
      if (n.status === "queued") c.mail.deliver(n);
      c.audit(u, "Resent email", "Email", n.id, `${n.template} → ${n.to}`);
      return pubLog(n);
    },

    async adminEmailTemplates(): Promise<T.EmailTemplateInfo[]> {
      c.principal("admin");
      return TEMPLATE_KEYS.map((k) => {
        const t = CATALOG.templates[k];
        return { key: k, category: t.category, essential: t.essential, pref: t.pref,
          subject: { en: renderTemplate(k, "en", t.sample).subject, ar: renderTemplate(k, "ar", t.sample).subject } };
      });
    },

    async previewEmailTemplate(key: string, lang: T.Lang | "bi" = "en"): Promise<T.EmailPreview> {
      c.principal("admin");
      const t = CATALOG.templates[key];
      if (!t) throw E.notFound("template not found");
      if (!["en", "ar", "bi"].includes(lang)) throw E.field("lang", "must be en, ar or bi");
      const data: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(t.sample)) data[k] = typeof v === "string" && v.startsWith("https://starthr.example") ? c.baseUrl + v.slice("https://starthr.example".length) : v;
      const out = renderTemplate(key, lang, data, t.essential ? "" : c.baseUrl + "/api/v1/public/email/unsubscribe?t=preview");
      return { key, lang: out.lang, subject: out.subject, preheader: out.preheader, html: out.html, text: out.text };
    },

    async adminEmailDns(q: { provider?: string; region?: string; from?: string } = {}): Promise<T.DnsGuidance> {
      c.principal("admin");
      const e = c.root.emailCfg;
      let pc = { provider: e.provider.provider as string, region: e.provider.region };
      let from = e.fromAddress;
      if (q.provider) {
        if (!providerInfo(q.provider)) throw E.field("provider", "unknown provider");
        const n = normalizeProvider({ provider: q.provider as T.EmailProviderId, region: q.region ?? "", host: "", port: 0, security: "", authMethod: "", username: "", secret: "", domain: "", messageStream: "", endpoint: "" });
        pc = { provider: n.provider, region: n.region };
      }
      if (q.from && q.from.trim()) { if (!validAddress(q.from.trim())) throw E.field("from", "must be a valid email address"); from = q.from.trim(); }
      return dnsGuidance(from, pc);
    },

    async adminSuppressions(): Promise<T.Suppression[]> {
      c.principal("admin");
      return Object.values(c.root.suppressions).sort((a, b) => (a.createdAt !== b.createdAt ? (a.createdAt < b.createdAt ? 1 : -1) : a.email < b.email ? -1 : 1));
    },

    async addSuppression(email: string, reason: T.Suppression["reason"] = "manual", note = ""): Promise<T.Suppression> {
      const u = c.principal("admin");
      const em = str(email).trim().toLowerCase();
      const fe: Record<string, string> = {};
      if (!validAddress(em)) fe.email = "a valid email address is required";
      if (!["manual", "bounce", "complaint", "unsubscribe"].includes(reason)) fe.reason = "must be manual, bounce, complaint or unsubscribe";
      if (str(note).length > 300) fe.note = "max 300 characters";
      if (Object.keys(fe).length) throw E.validation(fe);
      const sp: T.Suppression = { email: em, reason, note: str(note).trim(), createdAt: nowIso() };
      c.root.suppressions[em] = sp;
      c.audit(u, "Suppressed email address", "Email", em, reason);
      return sp;
    },

    async removeSuppression(email: string): Promise<void> {
      const u = c.principal("admin");
      const em = str(email).trim().toLowerCase();
      if (!c.root.suppressions[em]) throw E.notFound("address is not suppressed");
      delete c.root.suppressions[em];
      c.audit(u, "Removed email suppression", "Email", em, "");
    },
  };
}
